import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Paper,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedMenuItem as MenuItem, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { Lock as LockIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LalKitabSubtabs from '../components/LalKitabSubtabs';
import LalKitabChart from '../components/lalKitab/LalKitabChart';
import AnalysisTabBar from '../components/AnalysisTabBar';
import PremiumTrialPopup from '../components/PremiumTrialPopup';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';
import { formatDisplayDate } from '../utils/dateDisplay';
import { normalizeLanguage } from '../i18n/languages';
import i18nInstance from '../i18n';
import { translateInline } from '../i18n/inlineTranslations';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const labelText = (_isHindi: boolean, en: string, hi: string) => translateInline(i18nInstance.resolvedLanguage || i18nInstance.language, en, hi);

const formatChartDate = (value: any) => {
  return formatDisplayDate(value, { fallback: 'N/A' });
};

const formatChartTime = (time: any) => {
  if (!time) return 'N/A';
  if (typeof time === 'string') return time;
  const hour = String(time.hour ?? 0).padStart(2, '0');
  const minute = String(time.minute ?? 0).padStart(2, '0');
  const second = String(time.second ?? 0).padStart(2, '0');
  return `${hour}:${minute}:${second}`;
};

const formatChartPlace = (place: any) => {
  if (!place) return 'N/A';
  if (typeof place === 'string') return place;
  return [place.city, place.state, place.country].filter(Boolean).join(', ') || 'N/A';
};

const unwrapChartPayload = (payload: any) => payload?.data?.chart || payload?.chart || payload?.data || payload;

const normalizeChartForDisplay = (chart: any) => {
  if (!chart) return chart;

  const birthDetails = chart.birthDetails || {};
  return {
    ...chart,
    birthDetails: {
      ...birthDetails,
      name: typeof birthDetails.name === 'object'
        ? birthDetails.name?.fullName || birthDetails.name?.name || 'Chart'
        : birthDetails.name,
      timeOfBirth: formatChartTime(birthDetails.timeOfBirth),
      place: formatChartPlace(birthDetails.placeOfBirth || birthDetails.place),
    },
  };
};

const LalKitabGuide: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { state: authState } = useAuth();
  const isHindi = i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi');

  const [charts, setCharts] = useState<any[]>([]);
  const [selectedChartId, setSelectedChartId] = useState('');
  const [selectedVarshChartId, setSelectedVarshChartId] = useState('');
  const [dob, setDob] = useState('');
  const [report, setReport] = useState<any>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPremiumLock, setShowPremiumLock] = useState(false);
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess({
    onShowPremiumLock: () => setShowPremiumLock(true)
  });

  const chartId = id || selectedChartId;

  const generateReport = async () => {
    if (!chartId) {
      setError('Please select a saved chart first.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setReport(null);
      setChartData(null);

      const chartRes = await fetch(`${API_BASE_URL}/charts/${chartId}?ayanamshaMode=LAHIRI`, {
        headers: {
          ...(authState.token ? { Authorization: `Bearer ${authState.token}` } : {})
        }
      });
      const chartPayload = await chartRes.json().catch(() => ({}));

      if (!chartRes.ok) {
        throw new Error(chartPayload?.message || labelText(isHindi, 'Unable to load chart', 'कुंडली लोड नहीं हो सकी'));
      }

      const loadedChart = unwrapChartPayload(chartPayload);
      setChartData(normalizeChartForDisplay(loadedChart));
      if (!dob && loadedChart?.birthDetails?.dateOfBirth) {
        setDob(String(loadedChart.birthDetails.dateOfBirth).slice(0, 10));
      }

      await checkFeatureAccess({
        featureKey: 'LAL_KITAAB',
        apiCall: (trialAccessToken?: string) => axios.post('/lal-kitab/report', {
          chartId,
          language: normalizeLanguage(i18n.resolvedLanguage || i18n.language)
        }, {
          headers: trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : undefined
        }),
        onSuccess: (reportPayload: any) => {
          setShowPremiumLock(false);
          setReport(reportPayload.data);
        }
      });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || labelText(isHindi, 'Unable to generate Lal Kitab report', 'लाल किताब रिपोर्ट बनाने में समस्या हुई'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id || !authState.token) return;
    fetch(`${API_BASE_URL}/charts/my-charts?page=1&limit=50&type=individual`, {
      headers: { Authorization: `Bearer ${authState.token}` }
    })
      .then((response) => response.json())
      .then((payload) => {
        const items = payload?.data?.charts || [];
        setCharts(items);
        if (items[0]?._id) {
          setSelectedChartId(items[0]._id);
          setDob(items[0]?.birthDetails?.dateOfBirth || '');
        }
      })
      .catch(() => setCharts([]));
  }, [id, authState.token]);

  useEffect(() => {
    if (chartId) generateReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartId, isHindi]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {id && <AnalysisTabBar chartId={id} active="lalKitab" />}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box>
              <Chip label={labelText(isHindi, 'Premium Lal Kitab module', 'प्रीमियम लाल किताब मॉड्यूल')} color="success" sx={{ mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {labelText(isHindi, 'Lal Kitab Advanced Report', 'लाल किताब एडवांस्ड रिपोर्ट')}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 780 }}>
                {labelText(
                  isHindi,
                  'Comprehensive house-based Lal Kitab analysis including Janam Kundli, Drishti, Varshphal, Age Periods, and safe 43-day remedies.',
                  'जन्म कुंडली, दृष्टि, वर्षफल, आयु काल और सुरक्षित 43-दिन उपायों सहित भाव-आधारित लाल किताब विश्लेषण।'
                )}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {!id && (
                <>
                  <TextField
                    select
                    label={labelText(isHindi, 'Birth Chart', 'जन्म कुंडली')}
                    value={selectedChartId}
                    size="small"
                    onChange={(event) => setSelectedChartId(event.target.value)}
                    sx={{ minWidth: 200 }}
                  >
                    {charts.map((chart) => (
                      <MenuItem key={chart._id} value={chart._id}>
                        {chart.birthDetails?.name || labelText(isHindi, 'Untitled Chart', 'बिना नाम की कुंडली')}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={labelText(isHindi, 'Varshphal (Optional)', 'वर्षफल (वैकल्पिक)')}
                    value={selectedVarshChartId}
                    size="small"
                    onChange={(event) => setSelectedVarshChartId(event.target.value)}
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="">{labelText(isHindi, 'None', 'कोई नहीं')}</MenuItem>
                    {charts.map((chart) => (
                      <MenuItem key={chart._id} value={chart._id}>
                        {chart.birthDetails?.name || labelText(isHindi, 'Untitled', 'बिना नाम')} ({labelText(isHindi, 'Varsh', 'वर्ष')})
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    type="date"
                    label={labelText(isHindi, 'Date of Birth', 'जन्म तिथि')}
                    value={dob}
                    size="small"
                    onChange={(event) => setDob(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                </>
              )}
              {(loading || trialLoading) && chartId ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {labelText(isHindi, 'Loading Lal Kitab...', 'लाल किताब लोड हो रही है...')}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {showPremiumLock && (
        <Alert
          severity="info"
          icon={<LockIcon />}
          action={
            <Button variant="contained" onClick={() => navigate('/pricing')}>
              Upgrade
            </Button>
          }
          sx={{ mb: 3 }}
        >
          {labelText(
            isHindi,
            'Your 4 free trial access are used. Upgrade to Kundlistar Premium for unlimited premium access.',
            'आपके 4 निःशुल्क प्रीमियम क्रेडिट उपयोग हो चुके हैं। असीमित प्रीमियम एक्सेस के लिए Kundlistar Premium लें।'
          )}
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {report && !loading && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {labelText(isHindi, 'Lal Kitab Birth Chart (North Indian)', 'लाल किताब जन्म चार्ट (उत्तर भारतीय)')}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      {report.method}
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      {chartData ? <LalKitabChart chartData={chartData} size={380} showHouseLabels /> : null}
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {labelText(isHindi, 'Chart Information', 'कुंडली जानकारी')}
                        </Typography>
                        <Typography variant="body2"><strong>{labelText(isHindi, 'Name', 'नाम')}:</strong> {chartData?.birthDetails?.name || labelText(isHindi, 'Chart', 'कुंडली')}</Typography>
                        <Typography variant="body2"><strong>{labelText(isHindi, 'Date', 'तिथि')}:</strong> {formatChartDate(chartData?.birthDetails?.dateOfBirth)}</Typography>
                        <Typography variant="body2"><strong>{labelText(isHindi, 'Time', 'समय')}:</strong> {chartData?.birthDetails?.timeOfBirth || 'N/A'}</Typography>
                        <Typography variant="body2"><strong>{labelText(isHindi, 'Location', 'स्थान')}:</strong> {chartData?.birthDetails?.place || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                          {labelText(isHindi, 'Houses and Planets', 'भाव और ग्रह')}
                        </Typography>
                        <Grid container spacing={1}>
                          {Object.entries(report.chart?.planetsByHouse || {}).map(([house, planets]: any) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={house}>
                              <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography fontWeight={600} variant="body2">{labelText(isHindi, 'House', 'भाव')} {house}</Typography>
                                <Typography variant="caption" color="text.secondary">{planets.join(', ')}</Typography>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={12}>
            <LalKitabSubtabs
              chartId={chartId}
              varshChartId={selectedVarshChartId}
              dob={dob || String(chartData?.birthDetails?.dateOfBirth || '').slice(0, 10)}
              authToken={authState.token || undefined}
              initialReport={report}
              isHindi={isHindi}
            />
          </Grid>

          <Grid size={12}>
            <Alert severity="warning">{report.disclaimer}</Alert>
          </Grid>
        </Grid>
      )}
      <PremiumTrialPopup
        open={popup.open}
        featureName={popup.featureName}
        used={popup.used}
        total={popup.total}
        remaining={popup.remaining}
        loading={trialLoading}
        onCancel={closePopup}
        onContinue={popup.nextAction}
      />
    </Container>
  );
};

export default LalKitabGuide;
